/**
 * EICR Observation Classification Engine (BS 7671 / IET Code of Practice)
 * 
 * Classifies electrical safety observations into:
 * - C1: Danger present, risk of injury. Immediate remedial action required.
 * - C2: Potentially dangerous - urgent remedial action required.
 * - C3: Improvement recommended - not a danger but improvement advised.
 * - FI: Further investigation required, without delay.
 *
 * Overall assessment logic:
 * - Any C1, C2, or FI observation forces overall assessment to "UNSATISFACTORY"
 * - C3 alone does not make the installation unsatisfactory
 */

export const OBSERVATION_CODES = {
  C1: 'C1',
  C2: 'C2',
  C3: 'C3',
  FI: 'FI'
};

export const CODE_LABELS = {
  C1: 'C1 - Danger Present',
  C2: 'C2 - Potentially Dangerous',
  C3: 'C3 - Improvement Recommended',
  FI: 'FI - Further Investigation Required'
};

export const CODE_SEVERITY = {
  C1: 4,
  C2: 3,
  FI: 2,
  C3: 1
};

/**
 * Determine the observation code based on the classification
 * @param {string} code - Raw code value
 * @returns {string|null} - Normalized code C1/C2/C3/FI or null
 */
export const normalizeCode = (code) => {
  if (!code) return null;
  const upper = String(code).toUpperCase().trim();
  if (upper.includes('C1')) return 'C1';
  if (upper.includes('C2')) return 'C2';
  if (upper.includes('C3')) return 'C3';
  if (upper.includes('FI') || upper.includes('F1')) return 'FI';
  return null;
};

/**
 * Check if a code forces an unsatisfactory overall assessment
 * @param {string} code - Observation code
 * @returns {boolean} - True if the code forces UNSATISFACTORY
 */
export const forcesUnsatisfactory = (code) => {
  const normalized = normalizeCode(code);
  return normalized === 'C1' || normalized === 'C2' || normalized === 'FI';
};

/**
 * Compute overall assessment from a list of observation codes
 * @param {Array<string>} codes - List of observation codes
 * @returns {Object} - { overallAssessment, forceReason }
 */
export const computeOverallAssessment = (codes = []) => {
  const normalized = codes
    .map(normalizeCode)
    .filter(Boolean);

  if (normalized.length === 0) {
    return {
      overallAssessment: 'SATISFACTORY',
      forceReason: null
    };
  }

  // Sort by severity descending
  const sorted = [...normalized].sort(
    (a, b) => CODE_SEVERITY[b] - CODE_SEVERITY[a]
  );

  const highest = sorted[0];

  if (forcesUnsatisfactory(highest)) {
    return {
      overallAssessment: 'UNSATISFACTORY',
      forceReason: `${CODE_LABELS[highest]}`
    };
  }

  // Only C3 codes present
  if (normalized.every(c => c === 'C3')) {
    return {
      overallAssessment: 'SATISFACTORY',
      forceReason: 'Only C3 improvement recommendations present'
    };
  }

  return {
    overallAssessment: 'SATISFACTORY',
    forceReason: null
  };
};

/**
 * Classify an observation with contextual guidance
 * @param {Object} observation - Observation with text/code
 * @returns {Object} - Enriched classification
 */
export const classifyObservation = (observation) => {
  const code = normalizeCode(observation?.code || observation?.observationCode);
  return {
    ...observation,
    code,
    label: CODE_LABELS[code] || '',
    forcesUnsatisfactory: forcesUnsatisfactory(code),
    severity: CODE_SEVERITY[code] || 0
  };
};

/**
 * Suggest remediation action guidance for a code
 * @param {string} code - Observation code
 * @returns {string} - Remedial guidance
 */
export const getRemedialGuidance = (code) => {
  const normalized = normalizeCode(code);
  switch (normalized) {
    case 'C1':
      return 'Immediate remedial action required. Isolate the danger where possible and inform the duty holder without delay. Do not use the installation until rectified.';
    case 'C2':
      return 'Urgent remedial action required to prevent a potentially dangerous situation. Arrange for rectification promptly to remove the risk.';
    case 'FI':
      return 'Further investigation required without delay. Determine the extent of the problem and whether any danger is present before continued use.';
    case 'C3':
      return 'Improvement recommended. Not necessary for immediate safety, but should be rectified to bring the installation to current standards.';
    default:
      return '';
  }
};

export default {
  OBSERVATION_CODES,
  CODE_LABELS,
  CODE_SEVERITY,
  normalizeCode,
  forcesUnsatisfactory,
  computeOverallAssessment,
  classifyObservation,
  getRemedialGuidance
};